/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — per-user preferences: snap toggle, per-team box
   visibility, and the node "card style" (shape / density / badge
   toggles). Same additive localStorage-seed + server-wins-once
   pattern as the old chart. NO hidden-member state (People Rebuild §3
   "Placement rules [by grant]" — removed, not ported: no
   `orgchart_hiddenMemberIds` read/write anywhere here).

   Line-routing style is GONE from here too (People Rebuild
   orthogonal-lines law, 2026-08-18: the Rounded/Square/Straight/Curved
   selector no longer exists, so there's nothing left to persist a
   pick for). This hook no longer READS or WRITES
   `orgchart_routing`/`specforma:orgchart:routing` at all — an old
   stored value (server `user_prefs` row or localStorage key) is simply
   never looked at again, no migration needed. */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Team } from '../../../../../shared/types';
import { Prefs } from '../../../../services/prefsService';
import { DEFAULT_NODE_STYLE, type NodeStylePrefs } from '../types';

const PREF_SNAP_KEY = 'orgchart_snap';
const PREF_TEAMBOXES_KEY = 'orgchart_teamBoxes';
const PREF_NODE_STYLE_KEY = 'orgchart_nodeStyle';
// Legend-overlay visibility (Josh grant 2026-08-19: "show the legend on
// screen when not in edit mode") — additive key, same persistence pattern
// as the other toggles above.
const PREF_LEGEND_KEY = 'orgchart_legendVisible';

const SNAP_LS_KEY = 'specforma:orgchart:snap';
const TEAM_BOXES_LS_KEY = 'specforma:orgchart:teamBoxes';
const NODE_STYLE_LS_KEY = 'specforma:orgchart:nodeStyle';
const LEGEND_LS_KEY = 'specforma:orgchart:legendVisible';

function readNodeStyle(): NodeStylePrefs {
  if (typeof window === 'undefined') return DEFAULT_NODE_STYLE;
  try {
    const raw = window.localStorage.getItem(NODE_STYLE_LS_KEY);
    if (!raw) return DEFAULT_NODE_STYLE;
    return { ...DEFAULT_NODE_STYLE, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_NODE_STYLE;
  }
}

export function useOrgChartPrefs(teams: Team[]) {
  const [snapEnabled, setSnapEnabledState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(SNAP_LS_KEY) !== 'false';
  });
  const setSnapEnabled = useCallback((v: boolean) => {
    setSnapEnabledState(v);
    try {
      window.localStorage.setItem(SNAP_LS_KEY, v ? 'true' : 'false');
    } catch {
      /* best-effort */
    }
    Prefs.set(PREF_SNAP_KEY, v);
  }, []);
  const snapEnabledRef = useRef(snapEnabled);
  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
  }, [snapEnabled]);

  const [legendVisible, setLegendVisibleState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(LEGEND_LS_KEY) !== 'false';
  });
  const setLegendVisible = useCallback((v: boolean) => {
    setLegendVisibleState(v);
    try {
      window.localStorage.setItem(LEGEND_LS_KEY, v ? 'true' : 'false');
    } catch {
      /* best-effort */
    }
    Prefs.set(PREF_LEGEND_KEY, v);
  }, []);

  const [visibleTeamIds, setVisibleTeamIdsState] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem(TEAM_BOXES_LS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : {};
    } catch {
      return {};
    }
  });
  const setTeamBoxVisible = useCallback((teamId: string, visible: boolean) => {
    setVisibleTeamIdsState((prev) => {
      const next = { ...prev, [teamId]: visible };
      if (!visible) delete next[teamId];
      try {
        window.localStorage.setItem(TEAM_BOXES_LS_KEY, JSON.stringify(next));
      } catch {
        /* best-effort */
      }
      Prefs.set(PREF_TEAMBOXES_KEY, next);
      return next;
    });
  }, []);
  const setAllTeamsVisible = useCallback(
    (visible: boolean) => {
      const next: Record<string, boolean> = {};
      if (visible) for (const t of teams) next[t.id] = true;
      setVisibleTeamIdsState(next);
      try {
        window.localStorage.setItem(TEAM_BOXES_LS_KEY, JSON.stringify(next));
      } catch {
        /* best-effort */
      }
      Prefs.set(PREF_TEAMBOXES_KEY, next);
    },
    [teams]
  );
  /** Bulk-replace the whole visibility map in one go — used when
   *  applying a saved org-chart VIEW's `config.teamBoxVisibility`
   *  (People Rebuild edit-session grant, 2026-08-17). */
  const setTeamBoxVisibilityMap = useCallback((next: Record<string, boolean>) => {
    setVisibleTeamIdsState(next);
    try {
      window.localStorage.setItem(TEAM_BOXES_LS_KEY, JSON.stringify(next));
    } catch {
      /* best-effort */
    }
    Prefs.set(PREF_TEAMBOXES_KEY, next);
  }, []);

  const [nodeStyle, setNodeStyleState] = useState<NodeStylePrefs>(readNodeStyle);
  const setNodeStyle = useCallback((patch: Partial<NodeStylePrefs>) => {
    setNodeStyleState((prev) => {
      const next = { ...prev, ...patch };
      try {
        window.localStorage.setItem(NODE_STYLE_LS_KEY, JSON.stringify(next));
      } catch {
        /* best-effort */
      }
      Prefs.set(PREF_NODE_STYLE_KEY, next);
      return next;
    });
  }, []);

  // Apply the server copy once `Prefs` finishes loading — overrides
  // the localStorage seed ONLY when a server value actually exists.
  const [prefsTick, setPrefsTick] = useState(0);
  useEffect(() => Prefs.subscribe(() => setPrefsTick((v) => v + 1)), []);
  const appliedRef = useRef(false);
  useEffect(() => {
    if (appliedRef.current || !Prefs.isReady()) return;
    appliedRef.current = true;
    // Note: PREF_ROUTING_KEY ('orgchart_routing') is deliberately never
    // read here anymore — an old server-side value is ignored, not
    // migrated (People Rebuild orthogonal-lines law, 2026-08-18).
    const s = Prefs.get(PREF_SNAP_KEY);
    if (typeof s === 'boolean') setSnapEnabledState(s);
    const tb = Prefs.get(PREF_TEAMBOXES_KEY);
    if (tb && typeof tb === 'object') setVisibleTeamIdsState(tb);
    const ns = Prefs.get(PREF_NODE_STYLE_KEY);
    if (ns && typeof ns === 'object') setNodeStyleState((prev) => ({ ...prev, ...ns }));
    const lv = Prefs.get(PREF_LEGEND_KEY);
    if (typeof lv === 'boolean') setLegendVisibleState(lv);
  }, [prefsTick]);

  return {
    snapEnabled,
    setSnapEnabled,
    snapEnabledRef,
    visibleTeamIds,
    setTeamBoxVisible,
    setAllTeamsVisible,
    setTeamBoxVisibilityMap,
    nodeStyle,
    setNodeStyle,
    legendVisible,
    setLegendVisible,
  };
}
